// Theme-related type definitions and constants

// Core theme types - logical concepts without implementation details
export type ThemeColor = 'light' | 'sepia' | 'storm' | 'dark';
export type ThemeFont = 'lyon' | 'tisa' | 'helvetica' | 'georgia';
export type ThemeSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type ThemeWidth = 'sm' | 'md' | 'lg' | 'xl';
export type ReaderSidebarMode = 'highlights' | 'summary' | 'notes';

// Theme constants - single source of truth with stable order
export const THEME_COLORS: readonly ThemeColor[] = [
  'light',
  'sepia',
  'storm',
  'dark',
] as const;
export const THEME_FONTS: readonly ThemeFont[] = [
  'lyon',
  'tisa',
  'helvetica',
  'georgia',
] as const;
export const THEME_SIZES: readonly ThemeSize[] = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'xxl',
] as const;
export const THEME_WIDTHS: readonly ThemeWidth[] = ['sm', 'md', 'lg', 'xl'] as const;
export const READER_SIDEBAR_MODES: readonly ReaderSidebarMode[] = [
  'highlights',
  'summary',
  'notes',
] as const;

// CSS implementation types - how themes are applied
export type ThemeBodyClass = `theme-color-${ThemeColor}`;
export type FontBodyClass = `read-font-${ThemeFont}`;
export type SizeBodyClass = `read-text-${ThemeSize}`;
export type WidthBodyClass = `read-width-${ThemeWidth}`;

// Helper functions for converting logical themes to CSS classes
export function getThemeBodyClass(theme: ThemeColor): ThemeBodyClass {
  return `theme-color-${theme}`;
}

export function getFontBodyClass(font: ThemeFont): FontBodyClass {
  return `read-font-${font}`;
}

export function getSizeBodyClass(size: ThemeSize): SizeBodyClass {
  return `read-text-${size}`;
}

export function getWidthBodyClass(width: ThemeWidth): WidthBodyClass {
  return `read-width-${width}`;
}

// Legacy compatibility types (for existing code that uses the old format)
export type FontMode = ThemeFont;
export type WidthMode = ThemeWidth;

// Utility type for nullable values
type Nullable<T> = T | undefined | null;

// Theme configuration interface
export interface ThemeConfig {
  color: ThemeBodyClass;
  font: FontMode;
  size: ThemeSize;
  width: WidthMode;
  wpm: number;
}

// Theme settings interface for user preferences
export interface ThemeSettings extends ThemeConfig {
  showReaderSidebar: boolean;
  readerSidebarMode: Nullable<ReaderSidebarMode>;
}
